import React, { FC, useState } from 'react'
import SellerContact from '../seller/SellerContact'
import clsx from 'clsx'
import { Button } from 'src/components/ui'
import { AuctionIcon, BinIcon, ChevronDownIcon, CloseIcon, EditIcon } from '@svgs'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { IProperty } from 'src/interfaces/IProperty'
import { useDeletePropertyMutation, useGetUserQuery } from 'src/redux/api'
import PropertyParameters from 'src/components/elements/property-parameters/PropertyParameters'
import { pricePeriodKey } from 'src/helpers/pricePeriod'

interface IPropertyDetails {
  handleClose: () => void
  property: IProperty
}

const PropertyDetails: FC<IPropertyDetails> = ({ property, handleClose }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: user } = useGetUserQuery(undefined)
  const isOwner = !!user && user.id === property.user?.id
  const [isExpanded, setIsExpanded] = useState(false)
  const [deleteProperty, { isLoading: isDeleting }] = useDeletePropertyMutation()

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  const handleDelete = async () => {
    if (!window.confirm(t('delete_ad_confirm'))) return
    try {
      await deleteProperty(property.id).unwrap()
      handleClose()
    } catch {
      // delete errors are surfaced by the global api toast middleware
    }
  }

  const uah = property.price.find((el) => el.currency === 'UAH')
  const usd = property.price.find((el) => el.currency === 'USD')
  const eur = property.price.find((el) => el.currency === 'EUR')

  return (
    <section className={clsx('flex flex-col gap-5', !isOwner && 'pt-2')}>
      <div className="flex flex-col">
        <div className="flex flex-col items-start w-full">
          <div
            className={clsx(
              'flex items-center gap-3 self-end text-xs text-center sticky bg-surface z-10 rounded-lg',
              isOwner ? 'top-0' : 'top-2',
            )}
          >
            {isOwner ? (
              <div className="flex items-center gap-2">
                <Button
                  className="!h-8 !py-1 !gap-2 border-danger border-opacity-30 !fill-danger hover:!fill-white"
                  variant="outlined"
                  iconAfter={<BinIcon />}
                  onClick={handleDelete}
                  isLoading={isDeleting}
                >
                  {t('delete_the_ad')}
                </Button>
                <Button
                  className="!h-8 !py-1 !gap-2"
                  variant="outlined"
                  iconAfter={<EditIcon />}
                  onClick={() => {
                    handleClose()
                    navigate(`/edit-ad/${property.id}`)
                  }}
                >
                  {t('edit_your_ad')}
                </Button>
              </div>
            ) : (
              <Link
                to={`/announcement/${property.id}`}
                className="grow hover:text-brand border-b border-solid border-ink"
              >
                {t('go_to_the_ad')}
              </Link>
            )}
            <button
              onClick={() => handleClose()}
              className="icon-button fill-danger  w-4 h-4 hover:fill-danger-strong"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="mt-2.5 text-lg font-black leading-6 max-w-[30.75rem]">
            {/* {property.title} */}
            {`${property.addressSection.street}`}
          </div>
          <div className="flex flex-wrap gap-2 items-start self-stretch mt-4 text-xs text-center">
            <span className="px-4 py-2 whitespace-nowrap bg-brand-soft rounded-2xl text-center">
              {property.addressSection.settlement}
            </span>
            {!!property.addressSection.district && (
              <span className="px-4 py-2 bg-brand-soft rounded-2xl text-center">
                {property.addressSection.district}
              </span>
            )}
            <span className="px-4 py-2 bg-brand-soft rounded-2xl text-center">
              {property.addressSection.street}
            </span>
          </div>
          {property.auctionSection?.isAuction ? (
            <div className="mt-4 justify-start items-center gap-2.5 inline-flex flex-wrap">
              <div className="items-center gap-1 flex text-success text-lg font-black leading-[1.1069rem]">
                <div className="w-4 h-4 relative icon-button fill-ink">
                  <AuctionIcon />
                </div>
                <span>{t('announcement.auction')}</span>
              </div>
              <span className="px-1.5 py-[.1875rem] bg-success-strong rounded justify-center items-center gap-2.5 inline-flex text-white text-sm font-normal">
                {t('announcement.offer_your_price')}
              </span>
            </div>
          ) : (
            <div className="flex gap-3 mt-4 ">
              <div className="flex gap-2.5 items-start">
                <div className="flex flex-col">
                  <div className="flex gap-1 items-start">
                    <span className="text-lg font-black leading-none">{uah?.amount}</span>
                    <span className="text-sm">
                      {t('announcement.uah')}
                      {pricePeriodKey(property.listingSection.listingType) &&
                        ` ${t(pricePeriodKey(property.listingSection.listingType) as string)}`}
                    </span>
                  </div>
                  <div className="gap-2 text-sm">
                    ≈ <span className="font-semibold">{usd?.amount}</span>
                    <span className="font-semibold">$</span>
                    <span className="font-semibold"> / </span>≈{' '}
                    <span className="font-semibold">{eur?.amount}</span>
                    <span className="font-semibold">€</span>
                  </div>
                </div>
              </div>
              {property.parametersSection?.bargaining && (
                <span className="gap-2.5 self-start px-1.5 py-1 text-sm text-white whitespace-nowrap bg-success-strong rounded">
                  {t('bargaining')}
                </span>
              )}
            </div>
          )}
          <div className="mt-3.5 max-w-[25.375rem] w-full flex flex-col gap-2">
            <div
              className={clsx(
                'text-base leading-relaxed text-ink overflow-hidden relative',
                isExpanded ? 'max-h-full' : 'max-h-[7.875rem]',
              )}
            >
              <p className="whitespace-pre-line break-words">{property.description}</p>
            </div>
            <div onClick={toggleExpand}>
              <Button
                variant={'secondary'}
                className="!h-[1.875rem] !py-1 !gap-2 !bg-brand-soft !border-ink !border-opacity-10 text-ink fill-ink hover:fill-white hover:text-white hover:!border-brand hover:!border-opacity-100 hover:!bg-brand-strong"
                iconAfter={<ChevronDownIcon className={clsx(isExpanded ? 'rotate-180' : '')} />}
                iconClassName="!w-2 !h-2"
              >
                {isExpanded ? t('collapse') : t('full_description')}
              </Button>
            </div>
          </div>
          <SellerContact
            user={property.user}
            role={property.listingSection.ownershipType}
            isAuction={property.auctionSection?.isAuction}
            currentPrice={property.auctionSection?.currentPrice}
            currency={property.currency}
          />
        </div>
      </div>
      <div className="flex flex-col text-lg">
        <h2 className="self-start font-black leading-tight">{t('announcement.location')}</h2>
        <p className="self-start mt-2 text-sm">
          {[
            property.addressSection.settlement,
            property.addressSection.district,
            property.addressSection.street,
            property.addressSection.houseNumber,
          ]
            .filter(Boolean)
            .join(', ')}
        </p>
        <h2 className="self-start mt-4 font-black leading-tight">
          {t('announcement.property_info')}
        </h2>
        <PropertyParameters parameters={property.parametersSection} />
      </div>
    </section>
  )
}

export default PropertyDetails
